// Mercapoli - Admin Restaurants List
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Store, Search, Loader2, Plus, Edit, 
  Star, MapPin, Trash2, Building2, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { restaurantes as restaurantesApi, admin as adminApi, config as configApi } from '../../lib/api';
import { toast } from 'sonner';

const AdminRestaurants = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [comercios, setComercios] = useState([]);
  const [paises, setPaises] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [paisFiltro, setPaisFiltro] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, restaurant: null });

  useEffect(() => {
    loadData();
  }, [paisFiltro]);

  const loadData = async () => {
    try {
      const [paisesData, comerciosData] = await Promise.all([
        configApi.getPaises(),
        restaurantesApi.getAll()
      ]);
      
      setPaises(paisesData.paises);
      
      // Filtrar por país si es necesario
      if (paisFiltro !== 'all') {
        setComercios(comerciosData.filter(r => r.pais === paisFiltro));
      } else {
        setComercios(comerciosData);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar comercios');
    }
    setLoading(false);
  };

  const toggleEstado = async (comercioId, estadoActual) => {
    try {
      await adminApi.updateRestaurante(comercioId, { activo: !estadoActual });
      toast.success(`Restaurante ${!estadoActual ? 'activado' : 'desactivado'}`);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.restaurant) return;
    
    try {
      await adminApi.deleteRestaurante(deleteDialog.restaurant.comercio_id);
      toast.success('Restaurante desactivado');
      setDeleteDialog({ open: false, restaurant: null });
      loadData();
    } catch (error) {
      toast.error('Error al eliminar comercio');
    }
  };

  const filtered = comercios.filter(r =>
    !filtro || 
    r.nombre?.toLowerCase().includes(filtro.toLowerCase()) ||
    r.ciudad?.toLowerCase().includes(filtro.toLowerCase())
  );

  const getPaisNombre = (codigo) => {
    const pais = paises.find(p => p.codigo === codigo);
    return pais?.nombre || codigo;
  };

  const getPaisMoneda = (codigo) => {
    const pais = paises.find(p => p.codigo === codigo);
    return pais?.simbolo || '$';
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Comercios</h1>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{comercios.length} comercios</Badge>
              <Button 
                onClick={() => navigate('/admin/restaurantes/nuevo')}
                className="bg-brand-primary hover:bg-brand-secondary"
                data-testid="new-restaurant-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar comercio o ciudad..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="pl-10"
              data-testid="search-restaurants"
            />
          </div>
          
          <Select value={paisFiltro} onValueChange={setPaisFiltro}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filtrar por país" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los países</SelectItem>
              {paises.map(p => (
                <SelectItem key={p.codigo} value={p.codigo}>
                  {p.nombre} ({p.moneda})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Store className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No se encontraron comercios</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate('/admin/restaurantes/nuevo')}
            >
              <Plus className="h-4 w-4 mr-2" />
              Agregar primer comercio
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {filtered.map((rest) => (
              <Card key={rest.comercio_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <img 
                      src={rest.imagen || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=200'} 
                      alt={rest.nombre}
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-medium truncate">{rest.nombre}</h3>
                          {rest.es_sucursal && (
                            <Badge variant="outline" className="text-xs mt-1">
                              <Building2 className="h-3 w-3 mr-1" />
                              Sucursal
                            </Badge>
                          )}
                        </div>
                        <Badge className={rest.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {rest.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span>{rest.calificacion}</span>
                        <span className="mx-1">•</span>
                        <span>{rest.total_resenas || 0} reseñas</span>
                      </div>
                      
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <MapPin className="h-4 w-4" />
                        <span className="truncate">{rest.ciudad}, {getPaisNombre(rest.pais)}</span>
                      </div>
                      
                      <div className="text-sm text-muted-foreground mt-1">
                        Envío: {getPaisMoneda(rest.pais)}{rest.costo_envio} • {rest.comision_porcentaje}% comisión
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => navigate(`/admin/restaurantes/editar/${rest.comercio_id || rest.restaurante_id}`)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    
                    {!rest.es_sucursal && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/admin/restaurantes/nuevo?matriz=${rest.comercio_id || rest.restaurante_id}`)}
                      >
                        <Building2 className="h-4 w-4 mr-1" />
                        Sucursal
                      </Button>
                    )}
                    
                    <button
                      onClick={() => toggleEstado(rest.comercio_id, rest.activo)}
                      className={`p-2 rounded-lg transition-colors ${
                        rest.activo 
                          ? 'text-brand-accent hover:bg-brand-accent/10' 
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {rest.activo ? (
                        <ToggleRight className="h-5 w-5" />
                      ) : (
                        <ToggleLeft className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, restaurant: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar comercio?</AlertDialogTitle>
            <AlertDialogDescription>
              El comercio "{deleteDialog.restaurant?.nombre}" será desactivado y no aparecerá en la app.
              Puedes reactivarlo después.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRestaurants;
